import React from 'react';
import {
  CourseDesign,
  DesignAnswerOption,
  DesignConcept,
  DesignFlowEntry,
  DesignIntent,
} from '../../../services/designRunsService';
import { Card, Chip, LinesEditor, TextArea, TextField } from './editors';

function OptionsEditor({ options, editing, onChange }: {
  options: DesignAnswerOption[];
  editing: boolean;
  onChange: (next: DesignAnswerOption[]) => void;
}) {
  if (!editing) {
    return (
      <ul className="text-base ml-1 space-y-1">
        {options.map((o, i) => (
          <li key={i} className={`${o.correct ? 'text-green-700' : 'text-gray-600'} leading-snug`}>
            {o.correct ? '✅' : '◻️'} {o.text}
            {o.why && <span className="text-sm text-gray-400/80"> — {o.why}</span>}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className="space-y-1">
      {options.map((o, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={o.correct}
            title="correct"
            onChange={(e) => onChange(options.map((x, j) => (j === i ? { ...x, correct: e.target.checked } : x)))}
          />
          <input
            value={o.text}
            onChange={(e) => onChange(options.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
            className="flex-1 border border-gray-300 rounded px-2 py-0.5 text-sm"
          />
          <button className="text-xs text-red-500" onClick={() => onChange(options.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <button className="text-xs text-blue-600" onClick={() => onChange([...options, { text: '', correct: false }])}>
        + option
      </button>
    </div>
  );
}

function IntentBody({ intent, editing, assetBriefs, onChange }: {
  intent: DesignIntent;
  editing: boolean;
  assetBriefs: Record<string, string>;
  onChange: (next: DesignIntent) => void;
}) {
  const p = intent.payload || {};
  const setPayload = (patch: Partial<DesignIntent['payload']>) =>
    onChange({ ...intent, payload: { ...p, ...patch } });

  return (
    <div className="space-y-2">
      {editing ? (
        <TextArea label="Assessment goal" rows={2} value={intent.assessment_goal}
          onChange={(assessment_goal) => onChange({ ...intent, assessment_goal })} />
      ) : (
        <div className="text-base text-gray-700">{intent.assessment_goal}</div>
      )}

      {p.options && (
        <OptionsEditor options={p.options} editing={editing}
          onChange={(options) => setPayload({ options })} />
      )}
      {p.question_structure?.options && (
        <div>
          <div className="text-sm text-gray-500 mb-0.5" title={intent.stimulus}>
            🎧 After listening — {assetBriefs[intent.stimulus ?? ''] ?? intent.stimulus}
          </div>
          <OptionsEditor options={p.question_structure.options} editing={editing}
            onChange={(options) =>
              setPayload({ question_structure: { ...p.question_structure!, options } })} />
        </div>
      )}
      {p.frame !== undefined && (
        editing ? (
          <div className="space-y-1">
            <TextField label="Frame (contains the blank)" value={p.frame ?? ''}
              onChange={(frame) => setPayload({ frame })} />
            <TextField label="Answer" value={p.answer ?? ''}
              onChange={(answer) => setPayload({ answer })} />
            <LinesEditor label="Distractors" values={p.distractors ?? []} rows={2}
              onChange={(distractors) => setPayload({ distractors })} />
          </div>
        ) : (
          <div className="text-base text-gray-700">
            <div className="italic">{p.frame}</div>
            <div>✅ {p.answer} <span className="text-gray-400">| distractors: {(p.distractors ?? []).join(', ')}</span></div>
          </div>
        )
      )}
      {p.pairs && (
        <ul className="text-base text-gray-700 space-y-0.5">
          {p.pairs.map((pair, i) => (
            <li key={i}>
              {editing ? (
                <span className="flex gap-1 items-center">
                  <input value={pair.left} className="flex-1 border border-gray-300 rounded px-1 text-sm"
                    onChange={(e) => setPayload({ pairs: p.pairs!.map((x, j) => j === i ? { ...x, left: e.target.value } : x) })} />
                  →
                  <input value={pair.right} className="flex-1 border border-gray-300 rounded px-1 text-sm"
                    onChange={(e) => setPayload({ pairs: p.pairs!.map((x, j) => j === i ? { ...x, right: e.target.value } : x) })} />
                </span>
              ) : (
                <>{pair.left} <span className="text-gray-400">→</span> {pair.right}</>
              )}
            </li>
          ))}
        </ul>
      )}
      {p.task && <div className="text-base text-gray-700"><span className="text-sm text-gray-500">Task:</span> {p.task}</div>}

      {(intent.explanation_notes || editing) && (
        editing ? (
          <TextArea label="Explanation shown after answering" rows={2} value={intent.explanation_notes ?? ''}
            onChange={(explanation_notes) => onChange({ ...intent, explanation_notes })} />
        ) : (
          <div className="text-sm text-gray-400 leading-snug">After answering: {intent.explanation_notes}</div>
        )
      )}
    </div>
  );
}

function ConceptBody({ concept, editing, assetBriefs, onChange }: {
  concept: DesignConcept;
  editing: boolean;
  assetBriefs: Record<string, string>;
  onChange: (next: DesignConcept) => void;
}) {
  const items = concept.content?.items ?? [];
  return (
    <div className="space-y-2">
      {editing ? (
        <>
          <TextField label="Gist" value={concept.gist} onChange={(gist) => onChange({ ...concept, gist })} />
          <LinesEditor label="Teaching points" values={concept.teaching_points ?? []}
            onChange={(teaching_points) => onChange({ ...concept, teaching_points })} />
        </>
      ) : (
        <>
          <div className="text-base text-gray-800">{concept.gist}</div>
          {(concept.teaching_points ?? []).length > 0 && (
            <ul className="text-base text-gray-600 list-disc ml-4">
              {concept.teaching_points!.map((t) => <li key={t}>{t}</li>)}
            </ul>
          )}
        </>
      )}
      {items.length > 0 && (
        <ul className="text-base text-gray-700 space-y-0.5">
          {items.map((item, i) => (
            <li key={i}>
              {editing ? (
                <input value={item.text} className="w-full border border-gray-300 rounded px-1 text-sm"
                  onChange={(e) => onChange({
                    ...concept,
                    content: { ...concept.content, items: items.map((x, j) => j === i ? { ...x, text: e.target.value } : x) },
                  })} />
              ) : (
                <>
                  {item.group && <Chip tone="gray">{item.group}</Chip>} {item.text}
                  {item.meaning && <span className="text-gray-400"> — {item.meaning}</span>}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {concept.stimulus && (
        <div className="text-sm text-gray-400" title={concept.stimulus}>
          Media: {assetBriefs[concept.stimulus] ?? concept.stimulus}
        </div>
      )}
    </div>
  );
}

const PRACTICE_LABELS: Record<string, string> = {
  select_single: 'Choose the right one',
  select_multiple: 'Choose all that apply',
  map_pairs: 'Match',
  cloze: 'Fill the gap',
  listening: 'Listening',
  production_written: 'Write it yourself',
  production_spoken: 'Say it out loud',
  survey: 'Your take',
};

const CONCEPT_LABELS: Record<string, string> = {
  exposition: 'Explanation',
  phrase_bank: 'Phrase bank',
  worked_example: 'Worked example',
  scenario: 'Scenario',
  contrast: 'Do vs. don’t',
  tip: 'Tip',
};

/** One designed lesson: syllabus header + the full teach/practice flow. */
export function DossierLesson({ design, lessonNumber, editing, defaultOpen, onChange }: {
  design: CourseDesign;
  lessonNumber: number;
  editing: boolean;
  defaultOpen?: boolean;
  onChange: (next: CourseDesign) => void;
}) {
  const [open, setOpen] = React.useState(Boolean(defaultOpen));
  React.useEffect(() => { if (editing) setOpen(true); }, [editing]);
  const lesson = design.lessons.find((l) => l.lesson_number === lessonNumber);
  const syllabus = design.syllabus;
  const entryIdx = syllabus?.lessons.findIndex((l) => l.lesson_number === lessonNumber) ?? -1;
  const syllabusEntry = entryIdx >= 0 ? syllabus!.lessons[entryIdx] : undefined;

  const setSyllabusEntry = (patch: Record<string, unknown>) => {
    if (!syllabus || entryIdx < 0) return;
    const lessons = syllabus.lessons.map((l, i) => (i === entryIdx ? { ...l, ...patch } : l));
    onChange({ ...design, syllabus: { ...syllabus, lessons } });
  };

  const setEntry = (idx: number, entry: DesignFlowEntry) => {
    const lessons = design.lessons.map((l) =>
      l.lesson_number === lessonNumber
        ? { ...l, flow: l.flow.map((e, i) => (i === idx ? entry : e)) }
        : l,
    );
    onChange({ ...design, lessons });
  };

  const minutes = (syllabusEntry?.time_budget?.reading_minutes ?? 0)
    + (syllabusEntry?.time_budget?.exercises_minutes ?? 0);
  const assetBriefs = Object.fromEntries(
    design.assets.map((a) => [a.asset_id, a.brief?.split('.')[0] ?? a.asset_id]),
  );

  return (
    <div className="space-y-2">
      <Card className="bg-gray-50">
        <div className="flex items-baseline justify-between cursor-pointer select-none"
          onClick={() => !editing && setOpen(!open)}>
          <div className="text-base font-semibold text-gray-900">
            <span className="inline-block w-4 text-gray-400">{open ? '▾' : '▸'}</span>
            Lesson {lessonNumber} — {editing ? (
              <input value={syllabusEntry?.title ?? ''} className="border border-gray-300 rounded px-1 text-sm w-80"
                onChange={(e) => setSyllabusEntry({ title: e.target.value })} />
            ) : (syllabusEntry?.title ?? '')}
          </div>
          <span className="text-sm text-gray-400">
            ≈{minutes || '?'} min · {syllabusEntry?.time_budget?.exercise_count_target ?? '?'} exercises
          </span>
        </div>
        {editing ? (
          <div className="mt-2 space-y-2">
            <TextField label="Subtitle" value={syllabusEntry?.subtitle ?? ''}
              onChange={(subtitle) => setSyllabusEntry({ subtitle })} />
            <LinesEditor label="Learners will be able to" values={syllabusEntry?.objectives ?? []}
              onChange={(objectives) => setSyllabusEntry({ objectives })} />
          </div>
        ) : (
          <>
            {syllabusEntry?.subtitle && (
              <div className="text-sm text-gray-500">{syllabusEntry.subtitle}</div>
            )}
            {(syllabusEntry?.objectives ?? []).length > 0 && (
              <div className="text-base text-gray-600 mt-1">
                Learners will be able to: {syllabusEntry!.objectives.join(' · ')}
              </div>
            )}
          </>
        )}
      </Card>

      {open && !lesson && (
        <div className="text-sm text-gray-400 ml-1">Lesson content not designed yet.</div>
      )}
      {open && lesson?.flow.map((entry, idx) => (
        <Card key={entry.concept?.concept_id ?? entry.intent?.intent_id ?? idx}>
          <div className="flex items-center gap-2 mb-1.5"
            title={entry.concept?.concept_id ?? entry.intent?.intent_id}>
            {entry.concept ? (
              <Chip tone="gray">TEACH · {CONCEPT_LABELS[entry.concept.kind] ?? entry.concept.kind}</Chip>
            ) : (
              <Chip tone="indigo">
                PRACTICE · {PRACTICE_LABELS[entry.intent!.intent_type] ?? entry.intent!.intent_type}
              </Chip>
            )}
          </div>
          {entry.concept ? (
            <ConceptBody concept={entry.concept} editing={editing} assetBriefs={assetBriefs}
              onChange={(concept) => setEntry(idx, { concept })} />
          ) : (
            <IntentBody intent={entry.intent!} editing={editing} assetBriefs={assetBriefs}
              onChange={(intent) => setEntry(idx, { intent })} />
          )}
        </Card>
      ))}
    </div>
  );
}
